import React, { useMemo, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import MUIProvider from '~/contexts/MUIProvider';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import type ApiError from '~/types/ApiError';

import {
  CS_CONVERSATIONS_PAGE_ID,
  FILTER_DEFINITIONS,
  FREE_TEXT_FILTERS,
  type PageFilters,
  REPORT_VIEWS,
  type ReportView,
  type Row,
  VIEW_CARDS,
  csvOf,
  defaultFilters,
  filtersError,
  formatCell,
  isProblem,
  lensFilters,
  parseFilters,
  periodLabel,
  readCard,
  tableRows,
  totalsOf,
  writeFilters,
} from './model';
import { csConversationsPageView, editFromBridge } from './pageAdapter';

const LENS_ID = nativeLensId(CS_CONVERSATIONS_PAGE_ID);
const SOURCE = 'lux-analytics-prod.analytics · CS conversion models';
const ROW_CAP = 10000;
const PAGE_SIZES = [50, 100, 250];

const VIEW_NOTES: Record<ReportView, string> = {
  conversations:
    'One row per sales-queue conversation included in conversion scoring, dated by its adjusted start. Interaction Score Converted is 0 for a repeat contact with the same agent inside 30 days.',
  orders:
    'One row per order an agent placed for a customer. Conversions is 1 when the order is more than 30 days after the customer’s previous order.',
  excluded:
    'One row per sales-queue conversation excluded from conversion scoring by its wrap-up code (internal transfers, vendor follow-ups and similar).',
  'conversion-rate':
    'One row per final agent across the window. Sales Conversion Rate = SUM(conversions) / SUM(interaction score adjusted); Excluded Interactions % = SUM(excluded) / SUM(total interactions). Team Leader is the roster’s current pod leader for the agent.',
};

function download(name: string, contents: string) {
  const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8;' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function CsConversationsContent() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => parseFilters(new URLSearchParams(location.search)), [location.search]);
  const update = (change: Partial<PageFilters>) => {
    navigate({
      pathname: location.pathname,
      search: writeFilters({ ...filters, ...change }, new URLSearchParams(location.search)).toString(),
      hash: location.hash,
    });
  };
  const error = filtersError(filters);
  const request = useMemo(() => lensFilters(filters), [filters]);
  const query = useGetReportingLeLensData(error ? undefined : LENS_ID, request);
  const cards = query.data?.cards ?? [];
  // Filter values come back with each load; the last full list stays in place while the next one loads.
  const lastOptions = useRef<Record<string, Array<string>>>({});
  if (query.data?.filterOptions && Object.keys(query.data.filterOptions).length) {
    lastOptions.current = query.data.filterOptions;
  }
  const options = lastOptions.current;
  const ready = !error && query.isSuccess && !query.isFetching && !query.isShowingPreviousData;
  const failure = query.error as ApiError | null;

  const view = filters.reportView;
  const viewCards = VIEW_CARDS[view];
  const result = readCard(cards, viewCards.rows);
  const rows = useMemo(() => (isProblem(result) ? [] : tableRows(result, view)), [result, view]);
  const truncated = !isProblem(result) && result.truncated;
  const totals = totalsOf(cards, view);
  const range = periodLabel(isProblem(result) ? undefined : result.card, `${filters.from} to ${filters.to}`);
  const viewLabel = REPORT_VIEWS.find((entry) => entry.value === view)?.label ?? '';

  const pageView = csConversationsPageView({ ready, filters, options, period: range, totals, rows, truncated });
  usePageBridge({
    pageId: CS_CONVERSATIONS_PAGE_ID,
    view: pageView,
    edit: (changes) => {
      if (!ready) {
        throw new Error('Page data is still loading.');
      }
      update(editFromBridge(changes, pageView, filters));
    },
  });

  const gridColumns: Array<GridColDef<Row & { id: number }>> = viewCards.columns.map((column) => ({
    field: column.field,
    headerName: column.label,
    type: column.format === 'text' ? 'string' : 'number',
    width: column.width ?? 130,
    valueFormatter: (value: string | number | null) => formatCell(value, column.format),
  }));
  const gridRows = useMemo(() => rows.map((row, index) => ({ id: index, ...row })), [rows]);
  const totalColumns = viewCards.columns.filter(
    (column) => column.format !== 'text' && totals && column.field in totals,
  );
  const countId = viewCards.count;
  const rowCount = countId && totals ? totals[countId === 'order_count' ? 'orders' : 'conversations'] : null;

  const filterPanel = (
    <Stack gap={1.5} sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2">Filters</Typography>
      {FILTER_DEFINITIONS.filter((definition) => definition.view === view).map(({ key, label }) => (
        <Autocomplete<string, true, false, boolean>
          key={key}
          multiple
          size="small"
          freeSolo={FREE_TEXT_FILTERS.includes(key)}
          options={options[key] ?? []}
          value={filters.selections[key]}
          onChange={(_, value) => update({ selections: { ...filters.selections, [key]: value.map(String) } })}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder="All"
              helperText={FREE_TEXT_FILTERS.includes(key) ? 'Type an ID and press Enter' : undefined}
            />
          )}
        />
      ))}
    </Stack>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, md: 3 }, minWidth: 0 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={1}
        >
          <Box>
            <Typography component="h1" variant="h4" sx={{ fontSize: { xs: 25, sm: 30 }, color: tokens.accent }}>
              CS Conversations & Sales
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sales-queue conversations, attributed sales and conversion · Sydney dates · AUD
            </Typography>
          </Box>
          <PageChatButton />
        </Stack>
        <Tabs
          value={view}
          onChange={(_, reportView: ReportView) => update({ reportView })}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="CS Conversations & Sales dashboards"
        >
          {REPORT_VIEWS.map((entry) => (
            <Tab key={entry.value} value={entry.value} label={entry.label} />
          ))}
        </Tabs>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            size="small"
            type="date"
            label="P. Start Date"
            value={filters.from}
            onChange={(event) => update({ from: event.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="P. End Date"
            value={filters.to}
            onChange={(event) => update({ to: event.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <Button onClick={() => update({ from: defaultFilters(view).from, to: defaultFilters(view).to })}>
            Last 8 days
          </Button>
          <Chip size="small" variant="outlined" color="primary" label="Live" />
          <Typography variant="caption" color="text.secondary">
            {SOURCE} · rebuilt daily · {query.isFetching ? 'updating…' : range}
          </Typography>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {failure && (
          <Alert
            severity={failure.status === 404 || failure.status === 403 ? 'warning' : 'error'}
            action={
              <Button color="inherit" onClick={() => void query.refetch()}>
                Retry
              </Button>
            }
          >
            {failure.status === 404
              ? 'CS Conversations & Sales is not registered in Library for this environment yet. A reporting admin registers it under Library → Teams → Register native page.'
              : failure.status === 403
                ? 'You do not have access to CS Conversations & Sales. Ask a reporting admin to add you to its Library folder.'
                : failure.message || 'CS Conversations & Sales could not load.'}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 280px' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Stack gap={1.5} sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
              {VIEW_NOTES[view]}
            </Typography>
            {!error && !failure && query.isPending && <Skeleton variant="rounded" height={480} />}
            {!error && query.data && isProblem(result) && (
              <Alert severity={query.isFetching ? 'info' : 'error'}>
                {query.isFetching ? 'Loading…' : result.problem}
              </Alert>
            )}
            {!error && query.data && !isProblem(result) && (
              <>
                {totals && (
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: tokens.surface, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      Totals for the window
                      {rowCount !== null && rowCount !== undefined
                        ? ` · ${rowCount.toLocaleString('en-AU')} ${view === 'orders' ? 'orders' : 'conversations'}`
                        : ''}
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      {totalColumns.map((column) => (
                        <Box key={column.field} sx={{ minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                            {column.label}
                          </Typography>
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatCell(totals[column.field], column.format) || '–'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}
                {truncated && (
                  <Alert severity="warning">
                    This table reached its {ROW_CAP.toLocaleString('en-AU')}-row limit, so some rows are not shown.
                    Totals above still cover the whole window. Narrow the dates or filters to see every row.
                  </Alert>
                )}
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography variant="subtitle1">{viewLabel}</Typography>
                  <Button
                    size="small"
                    disabled={!ready || !rows.length}
                    onClick={() =>
                      download(
                        `cs-conversations-${view}-${filters.from}-${filters.to}.csv`,
                        csvOf(viewLabel, range, viewCards.columns, rows, truncated),
                      )
                    }
                  >
                    Download CSV
                  </Button>
                </Stack>
                <Box sx={{ height: 640, minWidth: 0 }}>
                  <DataGrid
                    rows={gridRows}
                    columns={gridColumns}
                    density="compact"
                    loading={query.isFetching}
                    disableRowSelectionOnClick
                    pageSizeOptions={PAGE_SIZES}
                    initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                  />
                </Box>
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere', pb: 2 }}>
              Source: {SOURCE} · {range}. Customer email and contact number are not shown. Blank means no value.
            </Typography>
          </Stack>
          <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
            {filterPanel}
          </Paper>
        </Box>
      </Stack>
    </Container>
  );
}

export default function CsConversationsPage() {
  return (
    <MUIProvider>
      <PageChatLayout>
        <CsConversationsContent />
      </PageChatLayout>
    </MUIProvider>
  );
}
